import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import SignatureCanvas from 'react-signature-canvas';
import { PDFDocument } from 'pdf-lib';
import {
  AVONDALE_FLU_DECLINATION,
  AVONDALE_FLU_PDF_FIELDS,
  AVONDALE_FLU_REASONS,
  type AvondaleFluDeclinationRequest,
  type AvondaleFluReason,
  normalizeAvondaleFluLanguage,
} from '../../shared/avondaleFluDeclination';

type AvondaleDocumentKind = 'flu' | 'caregiver' | 'wage';

const SUPPORTED_WAGE_NOTICE_LANGUAGES = new Set(['English', 'Spanish']);

interface AvondaleDocumentProps {
  kind: AvondaleDocumentKind;
}

interface FormState {
  name: string;
  employeeId: string;
  dateOfBirth: string;
  reasons: AvondaleFluReason[];
  otherReason: string;
  statement: string;
  signature: string;
  region: string;
  language: string;
}

const DOCUMENT_CONFIG = {
  flu: {
    title: AVONDALE_FLU_DECLINATION.title,
    templateName: AVONDALE_FLU_DECLINATION.templateName,
    endpoint: '/api/doc/avondale-flu/generate',
  },
  caregiver: {
    title: 'Caregiver Statement Form',
    templateName: 'Avondale Caregiver Statement Form',
    endpoint: '/api/doc/avondale-caregiver/generate',
  },
  wage: {
    title: 'Wage Notice Acknowledgment',
    templateName: '',
    endpoint: '/api/doc/avondale-wage-notice/generate',
  },
} satisfies Record<AvondaleDocumentKind, {
  title: string;
  templateName: string;
  endpoint: string;
}>;

function isAvondaleDomain(): boolean {
  const hostname = window.location.hostname;
  return hostname === 'portal.avondale.pro' || hostname === 'localhost' || hostname === '127.0.0.1';
}

function dateOnlyLabel(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : '';
}

function dateLabel(): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }).format(new Date());
}

function normalizePrimaryLanguage(value: string | null): string {
  const language = (value || '').trim();
  const normalized = language.toLowerCase();
  if (normalized === 'spanish' || normalized === 'es') return 'Spanish';
  if (normalized === 'english' || normalized === 'en') return 'English';
  return language || 'English';
}

function wageNoticeTemplateLanguage(primaryLanguage: string): string {
  return primaryLanguage === 'Spanish' ? 'Spanish' : 'English';
}

function signatureIsValid(signaturePad: SignatureCanvas | null, dataUrl: string): boolean {
  if (!signaturePad || signaturePad.isEmpty()) return false;
  const base64Length = dataUrl.split(',')[1]?.length ?? 0;
  return base64Length >= 250;
}

function revokeUrl(url: string | null): void {
  if (url) URL.revokeObjectURL(url);
}

function pdfObjectUrl(bytes: Uint8Array): string {
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return URL.createObjectURL(new Blob([buffer], { type: 'application/pdf' }));
}

async function applySignature(pdfDoc: PDFDocument, fieldName: string, signature: string): Promise<void> {
  if (!signature) return;
  try {
    const [, base64 = ''] = signature.split(',');
    const image = await pdfDoc.embedPng(base64);
    pdfDoc.getForm().getButton(fieldName).setImage(image);
  } catch {
    // Missing or non-button signature fields should not block local preview refresh.
  }
}

function setText(pdfDoc: PDFDocument, fieldName: string, value: string): void {
  try {
    pdfDoc.getForm().getTextField(fieldName).setText(value);
  } catch {
    // Template versions differ; preview should still render if an optional field is absent.
  }
}

function checkBox(pdfDoc: PDFDocument, fieldName: string): void {
  try {
    pdfDoc.getForm().getCheckBox(fieldName).check();
  } catch {
    // Template versions differ; preview should still render if an optional field is absent.
  }
}

function buildTemplateName(kind: AvondaleDocumentKind, region: string): string {
  if (kind === 'wage') return region ? `Avondale Wage Notice ${region}` : '';
  return DOCUMENT_CONFIG[kind].templateName;
}

export default function AvondaleDocument({ kind }: AvondaleDocumentProps) {
  const config = DOCUMENT_CONFIG[kind];
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const initialLanguage = normalizePrimaryLanguage(params.get('lang'));
  const regionParam = params.get('region')?.toLowerCase();
  const lockedRegion = regionParam === 'nyc'
    ? 'NYC'
    : regionParam === 'westchester'
      ? 'Westchester'
      : '';

  const [formState, setFormState] = useState<FormState>({
    name: params.get('name') || '',
    employeeId: params.get('id') || '',
    dateOfBirth: '',
    reasons: [],
    otherReason: '',
    statement: '',
    signature: '',
    region: lockedRegion,
    language: initialLanguage,
  });
  const [templateBytes, setTemplateBytes] = useState<Uint8Array | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [refreshingPreview, setRefreshingPreview] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const signaturePadRef = useRef<SignatureCanvas | null>(null);
  const previewJobRef = useRef(0);
  const signatureContainerRef = useRef<HTMLDivElement | null>(null);

  const templateName = buildTemplateName(kind, formState.region);
  const canFetchTemplate = kind !== 'wage' || Boolean(formState.region);
  const previewLanguage = kind === 'flu'
    ? normalizeAvondaleFluLanguage(formState.language)
    : wageNoticeTemplateLanguage(normalizePrimaryLanguage(formState.language));

  const updatePreview = useCallback(async (bytes: Uint8Array, nextState: FormState) => {
    const job = ++previewJobRef.current;
    setRefreshingPreview(true);
    try {
      const pdfDoc = await PDFDocument.load(bytes);
      if (kind === 'flu') {
        setText(pdfDoc, AVONDALE_FLU_PDF_FIELDS.name, nextState.name);
        setText(pdfDoc, AVONDALE_FLU_PDF_FIELDS.employeeId, nextState.employeeId);
        setText(pdfDoc, AVONDALE_FLU_PDF_FIELDS.dateOfBirth, dateOnlyLabel(nextState.dateOfBirth));
        setText(pdfDoc, AVONDALE_FLU_PDF_FIELDS.date, dateLabel());
        for (const reason of nextState.reasons) checkBox(pdfDoc, AVONDALE_FLU_PDF_FIELDS.reason(reason));
        if (nextState.reasons.includes('Other')) {
          setText(pdfDoc, AVONDALE_FLU_PDF_FIELDS.otherReason, nextState.otherReason);
        }
        await applySignature(pdfDoc, AVONDALE_FLU_PDF_FIELDS.signature, nextState.signature);
      } else if (kind === 'caregiver') {
        setText(pdfDoc, '{!Name}', nextState.name);
        setText(pdfDoc, '{!Statement}', nextState.statement);
        setText(pdfDoc, '{!Date}', dateLabel());
        await applySignature(pdfDoc, '{!Signature}', nextState.signature);
      } else {
        const primaryLanguage = normalizePrimaryLanguage(nextState.language);
        setText(pdfDoc, '{!FullName}', nextState.name);
        setText(pdfDoc, '{!FullName2}', nextState.name);
        setText(pdfDoc, '{!Primary_Language__c}', primaryLanguage);
        setText(pdfDoc, '{!Primary_Language__c2}', primaryLanguage);
        setText(pdfDoc, '{!SignatureDate}', dateLabel());
        setText(pdfDoc, '{!SignatureDate2}', dateLabel());
        const primaryLanguageDocument = SUPPORTED_WAGE_NOTICE_LANGUAGES.has(primaryLanguage);
        checkBox(pdfDoc, primaryLanguageDocument ? '{!DocIsPrimaryLanguage}' : '{!DocIsNotPrimaryLanguage}');
        checkBox(pdfDoc, primaryLanguageDocument ? '{!DocIsPrimaryLanguage2}' : '{!DocIsNotPrimaryLanguage2}');
        await applySignature(pdfDoc, '{!Signature}', nextState.signature);
        await applySignature(pdfDoc, '{!Signature2}', nextState.signature);
      }
      const pdfBytes = await pdfDoc.save();
      // A newer edit or template load started another preview; let it win.
      if (job !== previewJobRef.current) return;
      setPreviewUrl((oldUrl) => {
        revokeUrl(oldUrl);
        return pdfObjectUrl(pdfBytes);
      });
    } catch (previewError) {
      if (job === previewJobRef.current) {
        setError(previewError instanceof Error ? previewError.message : 'Failed to update preview');
      }
    } finally {
      if (job === previewJobRef.current) setRefreshingPreview(false);
    }
  }, [kind]);

  useEffect(() => {
    if (!canFetchTemplate || !templateName) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    setLoadingTemplate(true);
    setError('');

    const languageQuery = kind === 'wage' || kind === 'flu' ? `?language=${encodeURIComponent(previewLanguage)}` : '';
    fetch(`/api/doc/preview/${encodeURIComponent(templateName)}${languageQuery}`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error('Unable to load the document template');
        return new Uint8Array(await response.arrayBuffer());
      })
      .then(async (bytes) => {
        if (cancelled) return;
        setTemplateBytes(bytes);
      })
      .catch((loadError) => {
        if (!cancelled && loadError.name !== 'AbortError') {
          setError(loadError instanceof Error ? loadError.message : 'Unable to load the document template');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingTemplate(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [canFetchTemplate, formState.region, kind, previewLanguage, templateName]);

  useEffect(() => {
    if (!templateBytes) return;
    void updatePreview(templateBytes, formState);
  }, [
    formState.dateOfBirth,
    formState.employeeId,
    formState.language,
    formState.name,
    formState.otherReason,
    formState.reasons,
    formState.signature,
    formState.statement,
    templateBytes,
    updatePreview,
  ]);

  useEffect(() => () => {
    revokeUrl(previewUrl);
  }, [previewUrl]);

  useEffect(() => () => {
    revokeUrl(generatedUrl);
  }, [generatedUrl]);

  useEffect(() => {
    function resizeSignatureCanvas(): void {
      const pad = signaturePadRef.current;
      const container = signatureContainerRef.current;
      if (!pad || !container) return;
      const canvas = pad.getCanvas();
      const { width, height } = container.getBoundingClientRect();
      if (!width || !height) return;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      canvas.width = width;
      canvas.height = height;
      pad.off();
      pad.on();
    }

    const frame = window.requestAnimationFrame(resizeSignatureCanvas);
    window.addEventListener('resize', resizeSignatureCanvas);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', resizeSignatureCanvas);
    };
  }, [successMessage]);

  function setField(field: Exclude<keyof FormState, 'reasons'>, value: string): void {
    setFormState((current) => ({ ...current, [field]: value }));
    setError('');
    setSuccessMessage('');
  }

  function toggleReason(reason: AvondaleFluReason, checked: boolean): void {
    setFormState((current) => ({
      ...current,
      reasons: checked
        ? AVONDALE_FLU_REASONS.map((option) => option.value).filter((value) => value === reason || current.reasons.includes(value))
        : current.reasons.filter((value) => value !== reason),
    }));
    setError('');
    setSuccessMessage('');
  }

  function clearSignature(): void {
    signaturePadRef.current?.clear();
    setField('signature', '');
  }

  function validate(): boolean {
    if (kind === 'wage' && !formState.region) {
      setError('Region is required');
      return false;
    }
    if (!formState.name.trim()) {
      setError('Name is required');
      return false;
    }
    if (kind === 'flu' && !formState.dateOfBirth) {
      setError('Date of birth is required');
      return false;
    }
    if (kind === 'flu' && formState.reasons.length === 0) {
      setError('Select at least one reason');
      return false;
    }
    if (kind === 'flu' && formState.reasons.includes('Other') && !formState.otherReason.trim()) {
      setError('Please explain the other reason');
      return false;
    }
    if (kind === 'caregiver' && !formState.statement.trim()) {
      setError('Statement is required');
      return false;
    }
    if (!signatureIsValid(signaturePadRef.current, formState.signature)) {
      setError('Signature is required');
      return false;
    }
    return true;
  }

  async function submit(): Promise<void> {
    if (!validate()) return;
    setSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      const fluPayload: AvondaleFluDeclinationRequest = {
        name: formState.name,
        employeeId: formState.employeeId,
        dateOfBirth: formState.dateOfBirth,
        reasons: formState.reasons,
        otherReason: formState.reasons.includes('Other') ? formState.otherReason : '',
        language: normalizeAvondaleFluLanguage(formState.language),
        signature: formState.signature,
      };
      const payload = kind === 'flu'
        ? fluPayload
        : kind === 'caregiver'
          ? { name: formState.name, statement: formState.statement, signature: formState.signature }
          : {
              name: formState.name,
              region: formState.region,
              language: normalizePrimaryLanguage(formState.language),
              signature: formState.signature,
            };

      const response = await fetch(config.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok && response.status !== 207) throw new Error(data.message || 'Document submission failed');
      if (data.pdfBase64) {
        const binary = atob(data.pdfBase64);
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        setGeneratedUrl((oldUrl) => {
          revokeUrl(oldUrl);
          return pdfObjectUrl(bytes);
        });
      }
      setSuccessMessage(data.emailSent === false
        ? 'Document generated. Email delivery needs follow-up.'
        : 'Document generated and submitted successfully.');
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Document submission failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (!isAvondaleDomain()) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 2, md: 4 } }}>
        <Alert severity="error">You do not have permission to access this resource.</Alert>
      </Box>
    );
  }

  const pageTitle = kind === 'flu'
    ? 'Flu Declination Form'
    : kind === 'caregiver'
      ? 'Caregiver Statement Form'
      : 'Wage Notice Acknowledgment';
  const pageDescription = kind === 'flu'
    ? 'Please complete and sign this form to decline the flu vaccine.'
    : kind === 'caregiver'
      ? 'Please complete and sign this caregiver statement form.'
      : 'Please review, complete, and sign this wage notice acknowledgment.';

  if (successMessage) {
    return (
      <Container maxWidth="lg" sx={{ mt: 5, mb: 5 }}>
        <Paper elevation={3} sx={{ p: 3, bgcolor: '#f5f5f5' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: '#8900e1' }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Thank You!
            </Typography>
            <Typography variant="h6" color="text.secondary" align="center">
              {successMessage}
            </Typography>
          </Box>

          {generatedUrl && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" align="center" gutterBottom>
                Your Completed Document
              </Typography>
              <Paper elevation={2} sx={{ p: 2, bgcolor: 'white' }}>
                <Box
                  component="iframe"
                  src={generatedUrl}
                  title={`${config.title} completed PDF`}
                  sx={{ width: '100%', minHeight: { xs: 520, md: 900 }, border: 0 }}
                />
              </Paper>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = generatedUrl;
                    link.download = `${formState.name || 'Avondale'}_${pageTitle.replace(/\s+/g, '_')}.pdf`;
                    link.click();
                  }}
                >
                  Download Your Document
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Container>
    );
  }

  // Only block the page on the first template load; later language/region switches keep the
  // form (and the drawn signature) mounted while the new template loads.
  if (loadingTemplate && canFetchTemplate && !previewUrl) {
    return (
      <Container id="c1" maxWidth="md" sx={{ mt: 5, mb: 5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container id="c1" maxWidth="lg" sx={{ mt: 5 }}>
      <Paper
        elevation={3}
        sx={{
          p: 1,
          mb: 20,
          bgcolor: 'rgba(255,255,255,1.00)',
          px: { xs: 2, sm: 1 },
        }}
      >
        <Box sx={{ width: '100%', mt: 3 }}>
          <Typography variant="h4" align="center" gutterBottom sx={{ mt: 2 }}>
            {pageTitle}
          </Typography>
          <Typography variant="body1" align="center" paragraph>
            {pageDescription}
          </Typography>

          {error && <Alert severity="error" sx={{ mx: { xs: 0, sm: 1 }, mb: 2 }}>{error}</Alert>}

          <Box sx={{ mt: 3, mb: 3 }}>
            <Paper elevation={2} sx={{ p: 2, bgcolor: '#f5f5f5' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {previewUrl ? (
                  <Box
                    component="iframe"
                    src={previewUrl}
                    title={`${config.title} PDF`}
                    sx={{ width: '100%', minHeight: { xs: 520, md: 900 }, border: 0, bgcolor: 'white' }}
                  />
                ) : kind === 'wage' && !formState.region ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <Typography color="text.secondary">Select a region to preview the document.</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={refreshingPreview ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                  onClick={() => templateBytes && updatePreview(templateBytes, formState)}
                  disabled={!templateBytes || refreshingPreview}
                  sx={{
                    bgcolor: '#702b9d',
                    color: 'white',
                    '&:hover': { bgcolor: '#330662' },
                  }}
                >
                  {refreshingPreview ? 'Updating...' : 'Refresh Preview'}
                </Button>
              </Box>
            </Paper>
          </Box>

          <Box sx={{ m: { xs: 0.5, sm: 1 }, ml: { xs: 1 } }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 3 }}>
              {kind === 'wage' && !lockedRegion && (
                <FormControl fullWidth required>
                  <InputLabel id="avondale-region-label">Region</InputLabel>
                  <Select
                    labelId="avondale-region-label"
                    label="Region"
                    value={formState.region}
                    onChange={(event) => setField('region', event.target.value)}
                  >
                    <MenuItem value="NYC">NYC</MenuItem>
                    <MenuItem value="Westchester">Westchester</MenuItem>
                  </Select>
                </FormControl>
              )}

              <TextField
                label="Name"
                value={formState.name}
                onChange={(event) => setField('name', event.target.value)}
                required
                fullWidth
              />

              {kind === 'flu' && (
                <>
                  <FormControl fullWidth>
                    <InputLabel id="avondale-flu-language-label">Form Language</InputLabel>
                    <Select
                      labelId="avondale-flu-language-label"
                      label="Form Language"
                      value={normalizeAvondaleFluLanguage(formState.language)}
                      onChange={(event) => setField('language', event.target.value)}
                    >
                      <MenuItem value="English">English</MenuItem>
                      <MenuItem value="Spanish">Español</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label="Date of Birth"
                    type="date"
                    value={formState.dateOfBirth}
                    onChange={(event) => setField('dateOfBirth', event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                    fullWidth
                  />

                  <TextField
                    label="Employee ID"
                    value={formState.employeeId}
                    onChange={(event) => setField('employeeId', event.target.value)}
                    fullWidth
                  />

                  <FormControl component="fieldset" required>
                    <FormLabel component="legend">I am declining due to the following reasons (check all that apply)</FormLabel>
                    <FormGroup>
                      {AVONDALE_FLU_REASONS.map((reason) => (
                        <FormControlLabel
                          key={reason.value}
                          control={(
                            <Checkbox
                              checked={formState.reasons.includes(reason.value)}
                              onChange={(event) => toggleReason(reason.value, event.target.checked)}
                            />
                          )}
                          label={reason.label}
                        />
                      ))}
                    </FormGroup>
                    <FormHelperText>Select at least one reason.</FormHelperText>
                  </FormControl>

                  {formState.reasons.includes('Other') && (
                    <TextField
                      label="Other reason"
                      value={formState.otherReason}
                      onChange={(event) => setField('otherReason', event.target.value)}
                      inputProps={{ maxLength: 60 }}
                      required
                      fullWidth
                    />
                  )}
                </>
              )}

              {kind === 'caregiver' && (
                <TextField
                  label="Statement"
                  value={formState.statement}
                  onChange={(event) => setField('statement', event.target.value)}
                  required
                  fullWidth
                  multiline
                  rows={4}
                />
              )}
            </Box>

            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Signature
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Please sign in the box below
              </Typography>
              <Box ref={signatureContainerRef} sx={{ border: '1px solid #000000', width: '100%', height: 200, mb: 2 }}>
                <SignatureCanvas
                  ref={signaturePadRef}
                  onEnd={() => {
                    const pad = signaturePadRef.current;
                    if (!pad || pad.isEmpty()) return;
                    setField('signature', pad.getCanvas().toDataURL('image/png'));
                  }}
                  canvasProps={{ className: 'signatureCanvas', style: { width: '100%', height: '100%' } }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button variant="outlined" color="secondary" onClick={clearSignature}>
                  Clear Signature
                </Button>
                <Button
                  variant="contained"
                  startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                  disabled={submitting || loadingTemplate || !canFetchTemplate}
                  onClick={submit}
                >
                  Submit
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}
